import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GalleryItem, ViewerState } from '../../types/gallery';
import { scrollToTopInstant } from '../../utils/scrollRestoration';
import './ArtworkViewer.css';

interface ArtworkViewerProps {
  artwork: GalleryItem;
  getOptimizedUrl: (path: string, width?: number) => string;
}

export const ArtworkViewer: React.FC<ArtworkViewerProps> = ({ artwork, getOptimizedUrl }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<ViewerState>({
    currentArtwork: artwork,
    displayedImagePath: artwork.path,
    isAlternativeActive: false
  });

  const frameRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    scrollToTopInstant();
  }, [artwork.id]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let currentTiltFactor = 0;
    let smoothing = 0.08;
    let animId: number;

    const handleMouseMove = (e: MouseEvent) => {
      frame.classList.remove('resting');
      const rect = frame.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      targetX = (x / rect.width - 0.5) * 2;
      targetY = (y / rect.height - 0.5) * -2;
    };

    const handleMouseLeave = () => {
      frame.classList.add('resting');
      targetX = 0;
      targetY = 0;
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      smoothing = 0.12;
      frame.style.setProperty('--tilt-intensity', '6.5deg');
      frame.classList.remove('resting');
      const rect = frame.getBoundingClientRect();
      const touch = e.touches[0];
      targetX = ((touch.clientX - rect.left) / rect.width - 0.5) * 2;
      targetY = ((touch.clientY - rect.top) / rect.height - 0.5) * -2;
    };

    const handleTouchEnd = () => {
      frame.classList.add('resting');
      targetX = 0;
      targetY = 0;
    };

    frame.addEventListener('mousemove', handleMouseMove);
    frame.addEventListener('mouseleave', handleMouseLeave);
    frame.addEventListener('touchmove', handleTouchMove, { passive: false });
    frame.addEventListener('touchend', handleTouchEnd);

    const animateTilt = () => {
      currentX += (targetX - currentX) * smoothing;
      currentY += (targetY - currentY) * smoothing;

      const distance = Math.sqrt(currentX * currentX + currentY * currentY);
      const normalizedPressure = distance / 1.414;
      currentTiltFactor += (normalizedPressure - currentTiltFactor) * smoothing;

      frame.style.setProperty('--mouse-x', currentX.toFixed(3));
      frame.style.setProperty('--mouse-y', currentY.toFixed(3));
      frame.style.setProperty('--tilt-factor', currentTiltFactor.toFixed(3));

      animId = requestAnimationFrame(animateTilt);
    };

    animId = requestAnimationFrame(animateTilt);

    return () => {
      cancelAnimationFrame(animId);
      frame.removeEventListener('mousemove', handleMouseMove);
      frame.removeEventListener('mouseleave', handleMouseLeave);
      frame.removeEventListener('touchmove', handleTouchMove);
      frame.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const handleSwapAlt = useCallback(
    (altPath: string, altIndex: number) => {
      const update = () => {
        setState((prev) => ({
          ...prev,
          displayedImagePath: altPath,
          isAlternativeActive: true,
          activeAltIndex: altIndex
        }));
      };

      if (!document.startViewTransition) {
        update();
      } else {
        document.startViewTransition(update);
      }
    },
    []
  );

  const handleUndoSwap = useCallback(() => {
    const update = () => {
      setState((prev) => ({
        ...prev,
        displayedImagePath: artwork.path,
        isAlternativeActive: false,
        activeAltIndex: undefined
      }));
    };

    if (!document.startViewTransition) {
      update();
    } else {
      document.startViewTransition(update);
    }
  }, [artwork.path]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (state.isAlternativeActive) {
          handleUndoSwap();
        } else {
          navigate('/', { state: { fromReturnLink: true } });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isAlternativeActive, handleUndoSwap, navigate]);

  let cleanTitle = artwork.id;
  if (cleanTitle.includes('untitled')) cleanTitle = '[untitled]';
  cleanTitle = cleanTitle.replace(/-qm/g, '?').replace(/-c/g, ':');

  const specParts = artwork.dimensions.split(', ');
  const pixelCount = specParts[0];
  const extension = specParts[1] || '';

  return (
    <div className="artwork-viewer-root">
      <div id="return">
        <Link to="/" state={{ fromReturnLink: true }}>❮ Back to Exhibition</Link>
      </div>

      <div className="main-container">
        <div id="image-frame" ref={frameRef}>
          <img
            id="main-image"
            src={getOptimizedUrl(state.displayedImagePath, 1200)}
            alt={`schzstrg - ${cleanTitle} (${artwork.year})`}
          />
        </div>

        <div id="museum-card">
          <h2>
            <span id="artist-name">schzstrg</span> (b. 2003)
          </h2>
          <h2>
            <span id="art-title">{cleanTitle},</span> <span id="art-year">{artwork.year}</span>
          </h2>
          <h3>
            <span id="art-medium">{artwork.medium}</span>
          </h3>
          <h3 id="art-details">
            <span id="pixel-count">{pixelCount}</span>
            {extension && <>, <span id="extension">{extension}</span></>}
          </h3>
          {artwork.description && (
            <p
              id="art-description"
              style={{ display: 'block' }}
              dangerouslySetInnerHTML={{ __html: artwork.description }}
            />
          )}
        </div>
      </div>

      {artwork.altVersions && artwork.altVersions.length > 0 && (
        <div id="gallery-grid">
          {artwork.altVersions.map((altPath, idx) => {
            const isSwapped = state.isAlternativeActive && state.activeAltIndex === idx;
            return (
              <div key={altPath} className="alt-wrapper">
                <img
                  src={getOptimizedUrl(altPath, 1200)}
                  alt={`schzstrg - ${cleanTitle} (${artwork.year}) [alt version ${idx + 1}]`}
                  className={isSwapped ? 'swapped' : ''}
                  onClick={() => handleSwapAlt(altPath, idx)}
                />
                {isSwapped && (
                  <div
                    className="undo-x"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUndoSwap();
                    }}
                    title="Revert to original version"
                  >
                    ✕
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ArtworkViewer;
